import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PortableText } from '@portabletext/react';
import WeeklyPost from '../../components/WeeklyPost/WeeklyPost';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './SafetyVerseDetailPage.module.css';
import config from '../../config/config';
import { ISafetyPost } from '../../types/types';
import '../../components/PortableTextStyles.css';

const SafetyVerseDetailPage: React.FC = () => {
  const { verseId } = useParams<{ verseId: string }>();
  const navigate = useNavigate();
  const [safetyPost, setSafetyPost] = useState<ISafetyPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSafetyPostDetail = async () => {
      if (!verseId) {
        setError('No post ID provided.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(`Fetching safety post with ID: ${verseId}`);
        const response = await axios.get(`${config.API_BASE_URL}/safety-posts/${verseId}`);
        
        if (response.data) {
          console.log('Received safety post data:', response.data);
          setSafetyPost(response.data);
          setError(null);
        } else {
          setError(`Safety post with ID "${verseId}" not found.`);
          setSafetyPost(null);
        }
      } catch (error) {
        console.error('Error fetching safety post detail:', error);
        setError(`Safety post with ID "${verseId}" not found or could not be loaded.`);
        setSafetyPost(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSafetyPostDetail();
  }, [verseId]);

  const handleBackClick = () => {
    navigate('/safety-verses');
  };

  if (loading) {
    return (
      <div className={styles['safety-verse-detail-page']}>
        <div className={styles.loading}>Loading post details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['safety-verse-detail-page']}>
        <div className={styles.error}>
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Safety Team
          </button>
        </div>
      </div>
    );
  }

  if (!safetyPost) {
    return (
      <div className={styles['safety-verse-detail-page']}>
        <div className={styles.error}>
          <h2>Post Not Found</h2>
          <p>The requested safety post could not be found.</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Safety Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['safety-verse-detail-page']}>
      <div className={styles['safety-verse-header']}>
        <button className={styles['back-button']} onClick={handleBackClick}>
          ← Back to Safety Team
        </button>
        <h1>{safetyPost.reference}</h1>
      </div>

      <div className={styles['safety-verse-verses']}>
        <h2>Scripture</h2>
        <div className={styles['verse-text']}>
          {typeof safetyPost.verses === 'string' ? (
            <p>{safetyPost.verses}</p>
          ) : (
            <PortableText value={safetyPost.verses} />
          )}
        </div>
      </div>

      <div className={styles['safety-verse-teaching']}>
        <WeeklyPost
          title={safetyPost.title}
          date={safetyPost.date}
          content={safetyPost.content}
          type="safety"
          references={safetyPost.references || []}
        />
      </div>

      <ContactUs />
    </div>
  );
};

export default SafetyVerseDetailPage;
